/**
 * SVG Tools - Minifier, Converter, Component Generator
 * Reuses the same design system as the main converter app
 */
import JSZip from 'jszip';

export class SVGTools {
  private selectedFile: File | null = null;
  private mode: 'minify' | 'convert' | 'component' | 'optimize' | 'extended' | 'visual' | 'export' | 'sprite' | 'colors' | 'trace' | 'favicon' = 'minify';
  private spriteFiles: File[] = [];
  private rasterFile: File | null = null;
  private svgoPromise: Promise<any> | null = null;

  constructor() {
    this.render();
  }

  async render(): Promise<void> {
    const root = document.getElementById('app') || document.body;
    const cfg = (window as any).SVG_PAGE || {};
    // Landing pages mount only the interactive tool; they supply their own
    // header/hero/SEO content in static HTML around #app.
    root.innerHTML = cfg.toolOnly ? (this.getStyles() + this.getMainTool()) : this.getHTML();
    this.attachEventListeners();
    this.refreshSliders();

    // Initial tab: page config wins, then URL hash, else the default (minify).
    const configured = this.resolveMode(cfg.mode);
    if (configured) this.switchMode(configured);
    else this.applyHashRoute(false);

    window.addEventListener('hashchange', () => this.applyHashRoute(true));
  }

  // Map a slug/alias (from #hash or window.SVG_PAGE.mode) to a real tab id.
  private resolveMode(raw: string | undefined | null): typeof this.mode | null {
    if (!raw) return null;
    const aliases: Record<string, typeof this.mode> = {
      minify: 'minify',
      optimize: 'optimize', optimise: 'optimize', svgo: 'optimize',
      convert: 'convert',
      extended: 'extended', components: 'extended', react: 'extended', vue: 'extended',
      visual: 'visual',
      colors: 'colors', colours: 'colors', recolor: 'colors', recolour: 'colors',
      export: 'export', css: 'export', 'data-uri': 'export', datauri: 'export',
      favicon: 'favicon', favicons: 'favicon',
      sprite: 'sprite',
      trace: 'trace', 'png-to-svg': 'trace', 'jpg-to-svg': 'trace', 'image-to-svg': 'trace',
    };
    return aliases[String(raw).replace(/^#/, '').toLowerCase()] || null;
  }

  // Deep-link support: /#trace, /#png-to-svg, /#optimize, ... open the matching tab.
  private applyHashRoute(scroll: boolean): void {
    const mode = this.resolveMode(location.hash);
    if (!mode) return;
    this.switchMode(mode);
    if (scroll) document.getElementById('tool')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private getHTML(): string {
    return `
      ${this.getStyles()}
      <div class="page-wrapper">
        ${this.getNavigation()}
        ${this.getHeroSection()}
        ${this.getMainTool()}
        ${this.getFeaturesSection()}
        ${this.getFooter()}
      </div>
    `;
  }

  private getNavigation(): string {
    return `
      <nav class="navbar">
        <div class="container">
          <div class="nav-content">
            <a href="/" class="logo">ignlab.net</a>
            <div class="nav-links">
              <a href="/png-to-svg/" class="nav-link">PNG to SVG</a>
              <a href="/svg-to-react/" class="nav-link">SVG to React</a>
              <a href="/svg-minifier/" class="nav-link">SVG Minifier</a>
              <a href="/favicon-generator/" class="nav-link">Favicon</a>
              <a href="/svg-to-css/" class="nav-link">SVG to CSS</a>
            </div>
          </div>
        </div>
      </nav>
    `;
  }

  private getHeroSection(): string {
    return `
      <section class="hero">
        <div class="container">
          <div class="hero-content">
            <h1 class="hero-title">SVG Utilities</h1>
            <p class="hero-sub">
              Minify, convert, and generate code from SVG files.
              Strip metadata, convert to CSS/DataURI, or generate React components.
            </p>
            <div class="cta-row">
              <button class="btn btn-primary" onclick="document.getElementById('file-input').click()">
                <span>📁</span> Choose SVG File
              </button>
            </div>
            <div class="badges">
              <div class="badge">
                <span class="dot"></span>
                <span>Zero bloat SVGs</span>
              </div>
              <div class="badge">
                <span class="dot"></span>
                <span>CSS/DataURI ready</span>
              </div>
              <div class="badge">
                <span class="dot"></span>
                <span>React components</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    `;
  }

  private getMainTool(): string {
    return `
      <section class="tool-section" id="tool">
        <div class="container">
          <div class="tool-card">
            <div class="tool-header">
              <button class="tab tab-active" data-mode="minify">Minify</button>
              <button class="tab" data-mode="optimize">Optimize</button>
              <button class="tab" data-mode="convert">Convert</button>
              <button class="tab" data-mode="extended">Components</button>
              <button class="tab" data-mode="visual">Visual Editor</button>
              <button class="tab" data-mode="colors">Colors</button>
              <button class="tab" data-mode="export">Export</button>
              <button class="tab" data-mode="favicon">Favicon</button>
              <button class="tab" data-mode="sprite">Sprite</button>
              <button class="tab" data-mode="trace">PNG&rarr;SVG</button>
            </div>
            <div class="tool-body">
              <div class="drop-zone" id="drag-zone">
                <div class="drop-icon">📄</div>
                <div class="drop-title">Drag your SVG here</div>
                <div class="drop-sub">or click to browse</div>
              </div>
              <input type="file" id="file-input" class="file-input" accept=".svg,image/svg+xml">

              <div class="input-actions" id="input-actions">
                <button class="mini-btn" id="paste-toggle-btn">📋 Paste SVG code</button>
                <button class="mini-btn" id="paste-clipboard-btn">📥 Paste from clipboard</button>
                <button class="mini-btn" id="sample-btn">✨ Try a sample</button>
              </div>

              <div id="paste-area" class="panel-section hidden">
                <textarea id="paste-input" class="paste-input" placeholder="Paste your &lt;svg&gt;...&lt;/svg&gt; markup here, then click Load"></textarea>
                <button class="btn btn-primary" id="paste-load-btn" style="width:100%;margin-top:8px;">Load SVG</button>
              </div>

              <div id="live-preview" class="panel-section hidden">
                <div class="preview-grid">
                  <div class="preview-pane">
                    <div class="preview-pane-title">Preview</div>
                    <div id="source-preview" class="svg-preview-box"></div>
                  </div>
                  <div class="preview-meta" id="preview-meta"></div>
                </div>
              </div>

              <div id="file-info" class="panel-section hidden">
                <div class="file-size-info">
                  <div class="size-item">
                    <span class="size-label">Original Size</span>
                    <span class="size-value" id="original-size">—</span>
                  </div>
                  <div class="size-item">
                    <span class="size-label">Optimized Size</span>
                    <span class="size-value" id="optimized-size">—</span>
                  </div>
                  <div class="size-item">
                    <span class="size-label">Reduction</span>
                    <span class="size-value reduction" id="reduction-percent">—</span>
                  </div>
                </div>
              </div>

              <div id="minify-controls" class="panel-section">
                <label class="ctrl-label">Minification Level</label>
                <div class="slider-row">
                  <input type="range" id="minify-level" min="1" max="5" value="3" class="slider">
                  <span class="slider-value" id="minify-value">3</span>
                </div>
                <div class="check-row" style="margin-top: 12px;">
                  <input type="checkbox" id="remove-metadata" checked>
                  <label for="remove-metadata">Remove metadata & comments</label>
                </div>
              </div>

              <div id="convert-controls" class="panel-section hidden">
                <label class="ctrl-label">Output Format</label>
                <div class="select-wrap">
                  <select id="convert-format">
                    <option value="css">CSS background-image</option>
                    <option value="datauri">Data URI (Base64)</option>
                    <option value="inline">Inline SVG String</option>
                  </select>
                </div>
              </div>

              <div id="component-controls" class="panel-section hidden">
                <label class="ctrl-label">Framework</label>
                <div class="select-wrap">
                  <select id="component-framework">
                    <option value="react">React (JSX)</option>
                    <option value="vue">Vue 3</option>
                    <option value="html">HTML</option>
                  </select>
                </div>
                <div class="check-row" style="margin-top: 12px;">
                  <input type="checkbox" id="component-props" checked>
                  <label for="component-props">Extract props for size/color</label>
                </div>
              </div>

              <div id="optimize-controls" class="panel-section hidden">
                <label class="ctrl-label">Optimization Options</label>
                <div class="check-row" style="margin-bottom:10px;padding:10px;background:rgba(6,182,212,0.06);border:1px solid rgba(6,182,212,0.2);border-radius:8px;">
                  <input type="checkbox" id="opt-svgo" checked>
                  <label for="opt-svgo"><strong>Deep optimize with SVGO</strong> — path/transform crunching, much smaller output</label>
                </div>
                <div class="check-row">
                  <input type="checkbox" id="opt-comments" checked>
                  <label for="opt-comments">Remove comments</label>
                </div>
                <div class="check-row">
                  <input type="checkbox" id="opt-metadata" checked>
                  <label for="opt-metadata">Remove metadata</label>
                </div>
                <div class="check-row">
                  <input type="checkbox" id="opt-unused" checked>
                  <label for="opt-unused">Remove unused elements</label>
                </div>
                <div style="margin-top: 12px;">
                  <label class="ctrl-label">Decimal Precision</label>
                  <div class="slider-row">
                    <input type="range" id="opt-precision" min="0" max="6" value="2" class="slider">
                    <span class="slider-value" id="opt-precision-value">2</span>
                  </div>
                </div>
              </div>

              <div id="extended-controls" class="panel-section hidden">
                <label class="ctrl-label">Framework</label>
                <div class="select-wrap">
                  <select id="extended-framework">
                    <option value="react">React (JSX)</option>
                    <option value="vue">Vue 3</option>
                    <option value="svelte">Svelte</option>
                    <option value="flutter">Flutter (Dart)</option>
                    <option value="compose">Compose (Kotlin)</option>
                    <option value="swiftui">SwiftUI</option>
                  </select>
                </div>
                <div class="check-row" style="margin-top: 12px;">
                  <input type="checkbox" id="extended-props" checked>
                  <label for="extended-props">With component props</label>
                </div>
              </div>

              <div id="export-controls" class="panel-section hidden">
                <label class="ctrl-label">Format</label>
                <div class="select-wrap">
                  <select id="export-format">
                    <option value="png">PNG (supports transparency)</option>
                    <option value="jpeg">JPEG (white background)</option>
                    <option value="webp">WebP</option>
                  </select>
                </div>
                <label class="ctrl-label" style="margin-top:14px;">Scale</label>
                <div class="slider-row">
                  <input type="range" id="export-scale" min="1" max="4" value="2" step="0.5" class="slider">
                  <span class="slider-value" id="export-scale-value">2×</span>
                </div>
                <div class="check-row" style="margin-top:12px;">
                  <input type="checkbox" id="export-transparent" checked>
                  <label for="export-transparent">Transparent background</label>
                </div>
              </div>

              <div id="favicon-controls" class="panel-section hidden">
                <label class="ctrl-label">Favicon Package</label>
                <p style="font-size:0.82rem;color:var(--muted);margin-bottom:12px;">Generates every icon size a modern site needs, plus <code>favicon.ico</code>, a web manifest, and ready-to-paste HTML — all zipped.</p>
                <div class="favicon-sizes" id="favicon-sizes"></div>
                <div class="check-row" style="margin-top:12px;">
                  <input type="checkbox" id="favicon-padding">
                  <label for="favicon-padding">Add 10% safe-area padding</label>
                </div>
                <label class="ctrl-label" style="margin-top:14px;">Background</label>
                <div class="color-input-wrapper">
                  <input type="color" id="favicon-bg" value="#ffffff">
                  <input type="text" class="attr-input" id="favicon-bg-text" value="transparent" placeholder="transparent or #hex">
                </div>
              </div>

              <div id="trace-controls" class="panel-section hidden">
                <label class="ctrl-label">Raster → Vector (PNG / JPG → SVG)</label>
                <p style="font-size:0.82rem;color:var(--muted);margin-bottom:12px;">Upload a PNG or JPG and trace it into scalable SVG paths. Best for logos, icons, and flat art.</p>
                <button class="btn" id="raster-add-btn" style="width:100%;background:rgba(255,255,255,0.06);border:1px solid var(--border);color:var(--text);">
                  🖼️ Choose PNG / JPG
                </button>
                <input type="file" id="raster-input" class="file-input" accept="image/png,image/jpeg,image/webp,image/gif">
                <div id="raster-name" style="font-size:0.82rem;color:var(--muted);margin-top:8px;"></div>
                <label class="ctrl-label" style="margin-top:14px;">Detail Preset</label>
                <div class="select-wrap">
                  <select id="trace-preset">
                    <option value="detailed">Detailed (photos, gradients)</option>
                    <option value="flat" selected>Flat (logos, icons)</option>
                    <option value="poster">Posterized (bold shapes)</option>
                    <option value="bw">Black &amp; White (line art)</option>
                  </select>
                </div>
                <label class="ctrl-label" style="margin-top:14px;">Colors</label>
                <div class="slider-row">
                  <input type="range" id="trace-colors" min="2" max="32" value="8" class="slider">
                  <span class="slider-value" id="trace-colors-value">8</span>
                </div>
              </div>

              <div id="sprite-controls" class="panel-section hidden">
                <label class="ctrl-label">SVG Files</label>
                <div id="sprite-file-list" class="sprite-file-list"></div>
                <button class="btn" id="sprite-add-btn" style="width:100%;margin-top:8px;background:rgba(255,255,255,0.06);border:1px solid var(--border);color:var(--text);">
                  + Add SVG Files
                </button>
                <input type="file" id="sprite-input" class="file-input" accept=".svg" multiple>
                <div class="check-row" style="margin-top:12px;">
                  <input type="checkbox" id="sprite-prefix" checked>
                  <label for="sprite-prefix">Prefix IDs with filename</label>
                </div>
              </div>

              <div id="colors-controls" class="panel-section hidden">
                <div class="colors-split">
                  <div class="colors-left">
                    <label class="ctrl-label">Color Palette</label>
                    <p style="font-size:0.82rem;color:var(--muted);margin-bottom:12px;">Click any swatch to replace it.</p>
                    <div id="color-palette" class="color-palette"></div>
                    <div id="color-replace-form" class="color-replace-form hidden">
                      <div style="display:flex;gap:8px;align-items:center;margin-top:12px;">
                        <div id="color-replace-preview" class="color-swatch-large"></div>
                        <div style="flex:1;">
                          <div style="font-size:0.8rem;color:var(--muted);margin-bottom:4px;">Replace <span id="color-replace-source"></span> with:</div>
                          <div class="color-input-wrapper">
                            <input type="color" id="color-replace-picker">
                            <input type="text" class="attr-input" id="color-replace-text" placeholder="#000000">
                          </div>
                        </div>
                      </div>
                      <button class="btn btn-primary" id="color-replace-btn" style="width:100%;margin-top:10px;">Apply Color</button>
                    </div>
                  </div>
                  <div class="colors-right">
                    <div class="preview-title">Live Preview</div>
                    <div id="color-svg-preview" class="svg-preview-container">
                      <span style="color:var(--muted);font-size:0.82rem;">Load an SVG to see preview</span>
                    </div>
                  </div>
                </div>
              </div>

              <div id="visual-controls" class="panel-section hidden">
                <label class="ctrl-label">Visual Editing</label>
                <div class="select-wrap">
                  <select id="visual-operation">
                    <option value="edit-attributes">Edit Attributes (Live)</option>
                    <option value="viewbox-fixer">Auto-Crop ViewBox</option>
                  </select>
                </div>
              </div>

              <div id="attribute-editor" class="panel-section hidden">
                <label class="ctrl-label">Edit SVG Attributes</label>
                <div class="editor-split">
                  <div class="editor-left">
                    <div id="editor-controls"></div>
                  </div>
                  <div class="editor-right">
                    <div class="preview-title">Live Preview</div>
                    <div id="svg-preview" class="svg-preview-container"></div>
                  </div>
                </div>
                <div style="display: flex; gap: 12px; margin-top: 12px;">
                  <button id="apply-changes" class="btn btn-primary" style="flex: 1;">Apply Changes</button>
                  <button id="reset-changes" class="btn" style="flex: 1; background: rgba(255,255,255,0.1); border: 1px solid var(--border); color: var(--text);">Reset</button>
                </div>
              </div>

              <button class="action-btn" id="action-btn" disabled>
                <span class="spinner hidden" id="spinner"></span>
                <span>Process SVG</span>
              </button>

              <div id="output-section" class="panel-section hidden">
                <label class="ctrl-label">Output</label>
                <div class="output-box">
                  <pre><code id="output-code"></code></pre>
                </div>
                <div style="display: flex; gap: 10px; margin-top: 12px;">
                  <button class="btn btn-primary" style="flex: 1;" id="copy-btn">
                    📋 Copy to Clipboard
                  </button>
                  <button class="btn btn-primary" style="flex: 1;" id="download-btn">
                    ⬇ Download
                  </button>
                </div>
              </div>

              <div id="export-preview-section" class="panel-section hidden">
                <label class="ctrl-label">Preview</label>
                <div class="export-preview-box">
                  <img id="export-preview-img" src="" alt="Export preview">
                </div>
                <div id="export-preview-info" style="font-size:0.8rem;color:var(--muted);margin-top:8px;text-align:center;"></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    `;
  }

  private getFeaturesSection(): string {
    return `
      <section class="features-section" id="features-section">
        <div class="container">
          <div class="section-label">Features</div>
          <h2 class="section-title">What We Do</h2>
          <p class="section-sub">
            Remove bloat from design tool SVGs and prepare them for production.
          </p>
          <div class="features-grid">
            <div class="feature-card">
              <div class="feature-icon">✨</div>
              <div class="feature-title">Smart Minification</div>
              <p class="feature-desc">Strips metadata, editor junk, and unnecessary attributes while preserving visual integrity.</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">🎨</div>
              <div class="feature-title">CSS Conversion</div>
              <p class="feature-desc">Convert SVGs to production-ready CSS background-image properties and data URIs.</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">⚛️</div>
              <div class="feature-title">Component Export</div>
              <p class="feature-desc">Generate React, Vue, or HTML components from SVG files with optional props.</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">📊</div>
              <div class="feature-title">Size Preview</div>
              <p class="feature-desc">See before/after file sizes and calculate the reduction percentage instantly.</p>
            </div>
          </div>
        </div>
      </section>
    `;
  }

  private getFooter(): string {
    return `
      <footer class="footer">
        <div class="container">
          <p>&copy; 2026 ignlab.net - SVG Tools. Process locally, nothing stored.</p>
        </div>
      </footer>
    `;
  }

  private getStyles(): string {
    return `
      <style>
        /* CSS Variables */
        :root {
          --primary: #6366f1;
          --primary2: #06b6d4;
          --accent: #f59e0b;
          --success: #10b981;
          --glass: rgba(255,255,255,0.03);
          --border: rgba(255,255,255,0.08);
          --text: #f1f5f9;
          --muted: #94a3b8;
          --font: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          --radius: 12px;
          --container-width: 1200px;
        }

        /* Global Styles */
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        html, body {
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
          color: var(--text);
          font-family: var(--font);
          font-size: 16px;
          line-height: 1.6;
        }
        body { background-attachment: fixed; }

        /* Navbar */
        .navbar {
          background: rgba(15, 23, 42, 0.8);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid var(--border);
          position: sticky;
          top: 0;
          z-index: 100;
        }
        .navbar .container { padding: 0 24px; }
        .nav-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 14px 0;
        }
        .logo {
          font-size: 1.2rem;
          font-weight: 800;
          background: linear-gradient(135deg, var(--primary), var(--primary2));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          text-decoration: none;
        }
        .nav-links {
          display: flex;
          gap: 8px 20px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }
        .nav-link {
          font-size: 0.95rem;
          color: var(--muted);
          text-decoration: none;
          transition: color 0.2s;
        }
        .nav-link:hover, .nav-link.active {
          color: var(--primary2);
        }

        /* Container */
        .container {
          max-width: var(--container-width);
          margin: 0 auto;
          padding: 0 24px;
        }

        /* Hero */
        .hero { padding: 64px 0 48px; text-align: center; }
        .hero-content { max-width: 620px; margin: 0 auto; }
        .hero-title {
          font-size: clamp(2rem, 5vw, 3.2rem);
          font-weight: 800;
          letter-spacing: -0.02em;
          margin-bottom: 16px;
          background: linear-gradient(135deg, var(--primary), var(--primary2), var(--accent));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .hero-sub {
          font-size: 1.05rem;
          color: var(--muted);
          line-height: 1.7;
          max-width: 520px;
          margin: 0 auto 32px;
        }
        .cta-row { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 40px; justify-content: center; }

        /* Buttons */
        .btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 13px 24px;
          border-radius: 10px;
          font-size: 0.95rem;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: all 0.2s;
          text-decoration: none;
          white-space: nowrap;
        }
        .btn-primary {
          background: linear-gradient(135deg, var(--primary), var(--primary2));
          color: #fff;
          box-shadow: 0 4px 20px rgba(99,102,241,0.35);
        }
        .btn-primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 8px 28px rgba(99,102,241,0.45); }
        .btn-primary:disabled { opacity: 0.55; cursor: not-allowed; }

        /* Badges */
        .badges { display: flex; gap: 18px; flex-wrap: wrap; justify-content: center; margin-bottom: 20px; }
        .badge {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.82rem;
          color: var(--muted);
        }
        .badge .dot { width: 7px; height: 7px; border-radius: 50%; background: var(--success); flex-shrink: 0; }

        /* Tool Section */
        .tool-section { padding: 48px 0; }
        .tool-card {
          background: var(--glass);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid var(--border);
          border-radius: var(--radius);
          overflow: hidden;
          box-shadow: 0 24px 60px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.06);
        }
        .tool-header {
          display: flex;
          background: rgba(255,255,255,0.025);
          border-bottom: 1px solid var(--border);
        }
        .tab {
          flex: 1;
          padding: 14px 20px;
          text-align: center;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          background: none;
          border: none;
          color: var(--muted);
          transition: all 0.2s;
          border-bottom: 2px solid transparent;
        }
        .tab:hover { color: var(--text); }
        .tab.tab-active {
          color: var(--primary2);
          border-bottom-color: var(--primary2);
          background: rgba(6,182,212,0.06);
        }

        /* Tool Body */
        .tool-body { padding: 24px; }
        .drop-zone {
          border: 2px dashed rgba(99,102,241,0.3);
          border-radius: 12px;
          padding: 36px 24px;
          text-align: center;
          cursor: pointer;
          transition: all 0.25s;
          background: rgba(99,102,241,0.04);
          position: relative;
          overflow: hidden;
        }
        .drop-zone::after {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse 80% 60% at 50% 100%, rgba(99,102,241,0.08), transparent);
          pointer-events: none;
        }
        .drop-zone:hover, .drop-zone.dragover {
          border-color: var(--primary2);
          background: rgba(6,182,212,0.06);
          box-shadow: 0 0 0 4px rgba(6,182,212,0.08), inset 0 0 40px rgba(6,182,212,0.04);
        }
        .drop-icon { font-size: 2.4rem; margin-bottom: 10px; }
        .drop-title { font-weight: 700; font-size: 0.95rem; margin-bottom: 4px; }
        .drop-sub { font-size: 0.8rem; color: var(--muted); }
        input[type="file"].file-input { display: none; }

        /* File Size Info */
        .file-size-info {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          margin: 16px 0;
          padding: 12px;
          background: rgba(255,255,255,0.04);
          border-radius: 8px;
        }
        .size-item { text-align: center; }
        .size-label { display: block; font-size: 0.75rem; color: var(--muted); margin-bottom: 4px; }
        .size-value { display: block; font-size: 1.2rem; font-weight: 700; color: var(--primary2); }
        .size-value.reduction { color: var(--success); }

        /* Panel Sections */
        .panel-section { margin-top: 20px; }
        .panel-section.hidden { display: none !important; }
        /* Generic hide utility — also hides non-panel elements (drop zone, input
           actions) that switchMode toggles for the trace/sprite tabs. */
        .hidden { display: none !important; }
        .ctrl-label {
          display: block;
          font-size: 0.8rem;
          font-weight: 600;
          color: var(--muted);
          text-transform: uppercase;
          letter-spacing: 0.07em;
          margin-bottom: 8px;
        }

        /* Select */
        .select-wrap {
          position: relative;
        }
        .select-wrap::after {
          content: '▾';
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--muted);
          pointer-events: none;
        }
        select {
          width: 100%;
          padding: 10px 36px 10px 12px;
          background: rgba(255,255,255,0.05);
          border: 1px solid var(--border);
          border-radius: 9px;
          color: var(--text);
          font-family: var(--font);
          font-size: 0.9rem;
          appearance: none;
          cursor: pointer;
          transition: border-color 0.2s;
        }
        select:focus { outline: none; border-color: var(--primary); }
        select option { background: #1a2030; }

        /* Slider */
        .slider-row { display: flex; align-items: center; gap: 12px; }
        .slider-value { font-size: 0.95rem; font-weight: 700; color: var(--primary2); min-width: 36px; text-align: right; }
        input[type="range"] {
          flex: 1;
          -webkit-appearance: none;
          height: 5px;
          border-radius: 3px;
          background: rgba(255,255,255,0.1);
          outline: none;
          cursor: pointer;
          background-image: linear-gradient(var(--primary2), var(--primary2));
          background-size: 0% 100%;
          background-repeat: no-repeat;
        }
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 18px; height: 18px;
          border-radius: 50%;
          background: #fff;
          box-shadow: 0 2px 8px rgba(0,0,0,0.4), 0 0 0 2px var(--primary2);
          cursor: pointer;
          transition: box-shadow 0.2s;
        }
        input[type="range"]::-webkit-slider-thumb:hover { box-shadow: 0 2px 12px rgba(6,182,212,0.5), 0 0 0 3px var(--primary2); }

        /* Checkbox */
        .check-row {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 0.87rem;
          color: var(--muted);
          cursor: pointer;
        }
        .check-row input[type="checkbox"] {
          width: 16px; height: 16px;
          accent-color: var(--primary2);
          cursor: pointer;
        }

        /* Action Button */
        .action-btn {
          width: 100%;
          padding: 14px;
          font-size: 1rem;
          font-weight: 700;
          border-radius: 10px;
          border: none;
          cursor: pointer;
          background: linear-gradient(135deg, var(--primary), var(--primary2));
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.2s;
          box-shadow: 0 4px 20px rgba(99,102,241,0.35);
          margin-top: 20px;
        }
        .action-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 8px 28px rgba(99,102,241,0.45); }
        .action-btn:disabled { opacity: 0.55; cursor: not-allowed; }

        /* Spinner */
        .spinner {
          width: 16px; height: 16px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
          display: inline-block;
        }
        .spinner.hidden { display: none; }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* Output */
        .output-box {
          background: rgba(0,0,0,0.4);
          border: 1px solid var(--border);
          border-radius: 8px;
          padding: 12px;
          max-height: 300px;
          overflow-y: auto;
        }
        .output-box code {
          font-family: 'Monaco', 'Courier New', monospace;
          font-size: 0.85rem;
          color: var(--primary2);
          white-space: pre-wrap;
          word-break: break-all;
        }

        /* Features Section */
        .features-section { padding: 80px 0 64px; }
        .section-label {
          font-size: 0.78rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: var(--primary2);
          margin-bottom: 12px;
        }
        h2.section-title {
          font-size: clamp(1.6rem, 4vw, 2.4rem);
          font-weight: 800;
          letter-spacing: -0.02em;
          margin-bottom: 16px;
        }
        .section-sub { color: var(--muted); font-size: 1rem; line-height: 1.6; max-width: 560px; margin-bottom: 48px; }
        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 16px;
        }
        .feature-card {
          background: var(--glass);
          border: 1px solid var(--border);
          border-radius: var(--radius);
          padding: 28px 24px;
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .feature-card:hover { transform: translateY(-4px); border-color: var(--primary2); }
        .feature-icon { font-size: 2rem; margin-bottom: 12px; }
        .feature-title { font-weight: 700; margin-bottom: 8px; }
        .feature-desc { font-size: 0.95rem; color: var(--muted); line-height: 1.6; }

        /* Attribute Editor Split View */
        .editor-split {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-bottom: 12px;
        }
        .editor-left {
          min-width: 0;
        }
        .editor-right {
          display: flex;
          flex-direction: column;
          background: rgba(255,255,255,0.02);
          border: 1px solid var(--border);
          border-radius: 8px;
          padding: 12px;
        }
        .preview-title {
          font-weight: 600;
          color: var(--muted);
          font-size: 0.85rem;
          margin-bottom: 12px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .svg-preview-container {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 300px;
          background: rgba(0,0,0,0.3);
          border: 1px dashed var(--border);
          border-radius: 6px;
          overflow: auto;
          padding: 16px;
        }
        .svg-preview-container svg {
          max-width: 100%;
          max-height: 100%;
          filter: drop-shadow(0 2px 8px rgba(0,0,0,0.3));
        }

        /* Attribute Editor */
        .attr-group {
          margin-bottom: 20px;
          padding: 12px;
          background: rgba(255,255,255,0.02);
          border-radius: 8px;
          border: 1px solid var(--border);
        }
        .attr-title {
          font-weight: 600;
          color: var(--primary2);
          margin-bottom: 8px;
          font-size: 0.9rem;
        }
        .attr-input-group {
          margin-bottom: 10px;
        }
        .attr-label {
          display: flex;
          justify-content: space-between;
          font-size: 0.85rem;
          color: var(--muted);
          margin-bottom: 4px;
        }
        .attr-value {
          font-weight: 600;
          color: var(--text);
        }
        .attr-input {
          width: 100%;
          padding: 8px;
          background: rgba(255,255,255,0.05);
          border: 1px solid var(--border);
          border-radius: 6px;
          color: var(--text);
          font-family: var(--font);
          font-size: 0.9rem;
        }
        .attr-input:focus {
          outline: none;
          border-color: var(--primary2);
          background: rgba(6,182,212,0.1);
        }
        .color-input-wrapper {
          display: flex;
          gap: 8px;
          align-items: center;
        }
        .color-input-wrapper input[type="color"] {
          width: 50px;
          height: 35px;
          padding: 2px;
          border: 1px solid var(--border);
          border-radius: 6px;
          cursor: pointer;
        }
        .color-input-wrapper input[type="text"] {
          flex: 1;
        }

        /* Tab scroll */
        .tool-header { overflow-x: auto; scrollbar-width: none; }
        .tool-header::-webkit-scrollbar { display: none; }
        .tab { flex: 0 0 auto; }

        /* Sprite file list */
        .sprite-file-list {
          display: flex;
          flex-direction: column;
          gap: 6px;
          max-height: 180px;
          overflow-y: auto;
        }
        .sprite-file-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 7px 10px;
          background: rgba(255,255,255,0.04);
          border: 1px solid var(--border);
          border-radius: 6px;
          font-size: 0.85rem;
        }
        .sprite-file-name { color: var(--text); }
        .sprite-file-remove {
          background: none;
          border: none;
          color: var(--muted);
          cursor: pointer;
          font-size: 1rem;
          padding: 0 4px;
          line-height: 1;
        }
        .sprite-file-remove:hover { color: #f87171; }

        /* Colors split layout */
        .colors-split {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        .colors-left { min-width: 0; }
        .colors-right {
          display: flex;
          flex-direction: column;
          background: rgba(255,255,255,0.02);
          border: 1px solid var(--border);
          border-radius: 8px;
          padding: 12px;
          min-height: 300px;
        }
        @media (max-width: 600px) {
          .colors-split { grid-template-columns: 1fr; }
        }

        /* Color palette */
        .color-palette {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 4px;
        }
        .color-swatch {
          width: 36px;
          height: 36px;
          border-radius: 6px;
          border: 2px solid rgba(255,255,255,0.15);
          cursor: pointer;
          transition: transform 0.15s, border-color 0.15s;
          position: relative;
        }
        .color-swatch:hover { transform: scale(1.15); border-color: var(--primary2); }
        .color-swatch.selected { border-color: var(--primary2); transform: scale(1.1); }
        .color-swatch-large {
          width: 42px;
          height: 42px;
          border-radius: 8px;
          border: 2px solid rgba(255,255,255,0.2);
          flex-shrink: 0;
        }
        .color-replace-form { }

        /* Export preview */
        .export-preview-box {
          background: repeating-conic-gradient(rgba(255,255,255,0.05) 0% 25%, transparent 0% 50%) 0 0 / 16px 16px;
          border: 1px solid var(--border);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
          min-height: 120px;
        }
        .export-preview-box img {
          max-width: 100%;
          max-height: 300px;
          border-radius: 4px;
        }

        /* Input actions (paste / sample) */
        .input-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 12px;
          justify-content: center;
        }
        .mini-btn {
          padding: 8px 14px;
          font-size: 0.82rem;
          font-weight: 600;
          border-radius: 8px;
          border: 1px solid var(--border);
          background: rgba(255,255,255,0.04);
          color: var(--muted);
          cursor: pointer;
          transition: all 0.18s;
        }
        .mini-btn:hover { color: var(--text); border-color: var(--primary2); background: rgba(6,182,212,0.08); }
        .paste-input {
          width: 100%;
          min-height: 120px;
          padding: 12px;
          background: rgba(0,0,0,0.35);
          border: 1px solid var(--border);
          border-radius: 8px;
          color: var(--text);
          font-family: 'Monaco', 'Courier New', monospace;
          font-size: 0.82rem;
          resize: vertical;
        }
        .paste-input:focus { outline: none; border-color: var(--primary2); }

        /* Live preview + metadata */
        .preview-grid {
          display: grid;
          grid-template-columns: 1.1fr 0.9fr;
          gap: 14px;
        }
        @media (max-width: 600px) { .preview-grid { grid-template-columns: 1fr; } }
        .preview-pane {
          background: rgba(255,255,255,0.02);
          border: 1px solid var(--border);
          border-radius: 8px;
          padding: 12px;
        }
        .preview-pane-title {
          font-size: 0.72rem; font-weight: 700; text-transform: uppercase;
          letter-spacing: 0.07em; color: var(--muted); margin-bottom: 8px;
        }
        .svg-preview-box {
          display: flex; align-items: center; justify-content: center;
          min-height: 160px;
          background: repeating-conic-gradient(rgba(255,255,255,0.04) 0% 25%, transparent 0% 50%) 0 0 / 16px 16px;
          border-radius: 6px; padding: 12px; overflow: hidden;
        }
        .svg-preview-box svg { max-width: 100%; max-height: 220px; height: auto; filter: drop-shadow(0 2px 8px rgba(0,0,0,0.35)); }
        .preview-meta {
          display: flex; flex-direction: column; gap: 8px;
          align-content: start;
        }
        .meta-item {
          display: flex; justify-content: space-between; align-items: baseline;
          padding: 8px 12px;
          background: rgba(255,255,255,0.03);
          border: 1px solid var(--border);
          border-radius: 7px;
          font-size: 0.85rem;
        }
        .meta-label { color: var(--muted); }
        .meta-value { font-weight: 700; color: var(--primary2); font-family: 'Monaco','Courier New',monospace; }
        .meta-value.good { color: var(--success); }

        /* Favicon size chips */
        .favicon-sizes { display: flex; flex-wrap: wrap; gap: 6px; }
        .favicon-chip {
          font-size: 0.78rem; font-family: 'Monaco','Courier New',monospace;
          padding: 5px 9px; border-radius: 6px;
          background: rgba(99,102,241,0.1); border: 1px solid rgba(99,102,241,0.25);
          color: var(--text);
        }

        /* Footer */
        .footer {
          padding: 24px 0;
          text-align: center;
          color: var(--muted);
          border-top: 1px solid var(--border);
          margin-top: 64px;
        }
      </style>
    `;
  }

  private attachEventListeners(): void {
    // File upload
    const dragZone = document.getElementById('drag-zone');
    const fileInput = document.getElementById('file-input') as HTMLInputElement;

    if (dragZone) {
      dragZone.addEventListener('dragover', (e) => {
        e.preventDefault();
        dragZone.classList.add('dragover');
      });
      dragZone.addEventListener('dragleave', () => dragZone.classList.remove('dragover'));
      dragZone.addEventListener('drop', (e) => {
        e.preventDefault();
        dragZone.classList.remove('dragover');
        if (e.dataTransfer?.files[0]) {
          this.handleFileSelect(e.dataTransfer.files[0]);
        }
      });
      dragZone.addEventListener('click', () => fileInput?.click());
    }

    if (fileInput) {
      fileInput.addEventListener('change', (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) this.handleFileSelect(file);
      });
    }

    // Tab switching
    document.querySelectorAll('.tab').forEach(tab => {
      tab.addEventListener('click', (e) => {
        const mode = (e.target as HTMLElement).getAttribute('data-mode') as any;
        if (mode) this.switchMode(mode);
      });
    });

    // Keep every range slider's coloured fill in sync with its value (incl. dynamically-added ones).
    document.addEventListener('input', (e) => {
      const t = e.target as HTMLElement;
      if (t instanceof HTMLInputElement && t.type === 'range') this.updateSliderFill(t);
    });

    // Sliders
    const minifySlider = document.getElementById('minify-level') as HTMLInputElement;
    if (minifySlider) {
      minifySlider.addEventListener('input', (e) => {
        document.getElementById('minify-value')!.textContent = (e.target as HTMLInputElement).value;
      });
    }

    const precisionSlider = document.getElementById('opt-precision') as HTMLInputElement;
    if (precisionSlider) {
      precisionSlider.addEventListener('input', (e) => {
        document.getElementById('opt-precision-value')!.textContent = (e.target as HTMLInputElement).value;
      });
    }

    // Action button
    const actionBtn = document.getElementById('action-btn') as HTMLButtonElement;
    if (actionBtn) {
      actionBtn.addEventListener('click', () => this.processFile());
    }

    // Copy button
    const copyBtn = document.getElementById('copy-btn');
    if (copyBtn) {
      copyBtn.addEventListener('click', () => this.copyToClipboard());
    }

    // Download button
    const downloadBtn = document.getElementById('download-btn');
    if (downloadBtn) {
      downloadBtn.addEventListener('click', () => this.downloadOutput());
    }

    // Export scale slider
    const exportScale = document.getElementById('export-scale') as HTMLInputElement;
    if (exportScale) {
      exportScale.addEventListener('input', () => {
        document.getElementById('export-scale-value')!.textContent = exportScale.value + '×';
      });
    }

    // Sprite file add
    const spriteAddBtn = document.getElementById('sprite-add-btn');
    const spriteInput = document.getElementById('sprite-input') as HTMLInputElement;
    if (spriteAddBtn) spriteAddBtn.addEventListener('click', () => spriteInput?.click());
    if (spriteInput) {
      spriteInput.addEventListener('change', () => {
        Array.from(spriteInput.files || []).forEach(f => this.addSpriteFile(f));
        spriteInput.value = '';
      });
    }

    // Paste / sample inputs
    document.getElementById('paste-toggle-btn')?.addEventListener('click', () => {
      document.getElementById('paste-area')?.classList.toggle('hidden');
    });
    document.getElementById('paste-load-btn')?.addEventListener('click', () => {
      const ta = document.getElementById('paste-input') as HTMLTextAreaElement;
      if (ta?.value.trim()) {
        this.setSvgFromString(ta.value, 'pasted.svg');
        document.getElementById('paste-area')?.classList.add('hidden');
      }
    });
    document.getElementById('paste-clipboard-btn')?.addEventListener('click', async () => {
      try {
        const text = await navigator.clipboard.readText();
        if (text.trim()) this.setSvgFromString(text, 'pasted.svg');
        else alert('Clipboard is empty.');
      } catch {
        alert('Clipboard read was blocked. Use “Paste SVG code” and paste manually.');
      }
    });
    document.getElementById('sample-btn')?.addEventListener('click', () => {
      this.setSvgFromString(this.getSampleSVG(), 'sample.svg');
    });

    // Raster (trace) input
    const rasterAddBtn = document.getElementById('raster-add-btn');
    const rasterInput = document.getElementById('raster-input') as HTMLInputElement;
    rasterAddBtn?.addEventListener('click', () => rasterInput?.click());
    rasterInput?.addEventListener('change', () => {
      const f = rasterInput.files?.[0];
      if (f) {
        this.rasterFile = f;
        const nameEl = document.getElementById('raster-name');
        if (nameEl) nameEl.textContent = `🖼️ ${f.name} (${this.formatBytes(f.size)})`;
        const actionBtn = document.getElementById('action-btn') as HTMLButtonElement;
        if (actionBtn) actionBtn.disabled = false;
      }
      rasterInput.value = '';
    });

    // Trace colors slider
    const traceColors = document.getElementById('trace-colors') as HTMLInputElement;
    traceColors?.addEventListener('input', () => {
      document.getElementById('trace-colors-value')!.textContent = traceColors.value;
    });

    // Favicon background sync
    const favBg = document.getElementById('favicon-bg') as HTMLInputElement;
    const favBgText = document.getElementById('favicon-bg-text') as HTMLInputElement;
    favBg?.addEventListener('input', () => { if (favBgText) favBgText.value = favBg.value; });
  }

  private getSampleSVG(): string {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 24 24" fill="none" stroke="#6366f1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
  <path d="M12 2L2 7l10 5 10-5-10-5z"/>
  <path d="M2 17l10 5 10-5"/>
  <path d="M2 12l10 5 10-5"/>
</svg>`;
  }

  // Paint the coloured fill of a range slider from its own value (min→max).
  private updateSliderFill(el: HTMLInputElement): void {
    const min = parseFloat(el.min || '0');
    const max = parseFloat(el.max || '100');
    const val = parseFloat(el.value || '0');
    const pct = max > min ? Math.min(100, Math.max(0, ((val - min) / (max - min)) * 100)) : 0;
    el.style.backgroundSize = pct + '% 100%';
  }

  private refreshSliders(root: ParentNode = document): void {
    root.querySelectorAll<HTMLInputElement>('input[type="range"]').forEach(el => this.updateSliderFill(el));
  }

  private switchMode(mode: typeof this.mode): void {
    this.mode = mode;

    document.querySelectorAll('.tab').forEach(tab => tab.classList.remove('tab-active'));
    document.querySelector(`[data-mode="${mode}"]`)?.classList.add('tab-active');

    const panels = ['minify','convert','optimize','extended','visual','component','export','sprite','colors','trace','favicon'];
    panels.forEach(p => document.getElementById(`${p}-controls`)?.classList.toggle('hidden', mode !== p));

    const dragZone = document.getElementById('drag-zone');
    const inputActions = document.getElementById('input-actions');
    const livePreview = document.getElementById('live-preview');
    const fileInfo = document.getElementById('file-info');
    const actionBtn = document.getElementById('action-btn') as HTMLButtonElement;

    if (mode === 'sprite') {
      // Sprite manages its own file list
      dragZone?.classList.add('hidden');
      inputActions?.classList.add('hidden');
      livePreview?.classList.add('hidden');
      fileInfo?.classList.add('hidden');
      actionBtn.disabled = this.spriteFiles.length === 0;
      actionBtn.querySelector('span:last-child')!.textContent = 'Generate Sprite';
    } else if (mode === 'trace') {
      // Trace uses its own raster input, not the SVG drop zone
      dragZone?.classList.add('hidden');
      inputActions?.classList.add('hidden');
      livePreview?.classList.add('hidden');
      fileInfo?.classList.add('hidden');
      actionBtn.disabled = !this.rasterFile;
      actionBtn.querySelector('span:last-child')!.textContent = 'Trace to SVG';
    } else {
      dragZone?.classList.remove('hidden');
      inputActions?.classList.remove('hidden');
      livePreview?.classList.toggle('hidden', !this.selectedFile);
      actionBtn.disabled = !this.selectedFile;
      actionBtn.querySelector('span:last-child')!.textContent =
        mode === 'export' ? 'Export Image' :
        mode === 'favicon' ? 'Generate Favicon Package' :
        mode === 'colors' ? 'Apply Color Replacements' : 'Process SVG';
    }

    if (mode === 'colors' && this.selectedFile) this.extractAndShowColors();
    if (mode === 'favicon') this.renderFaviconSizes();

    // Reset output
    document.getElementById('output-section')?.classList.add('hidden');
    document.getElementById('export-preview-section')?.classList.add('hidden');
    document.getElementById('attribute-editor')?.classList.add('hidden');
  }

  private handleFileSelect(file: File): void {
    if (this.mode === 'sprite') {
      this.addSpriteFile(file);
      return;
    }
    this.selectedFile = file;
    document.getElementById('file-info')?.classList.remove('hidden');
    document.getElementById('original-size')!.textContent = this.formatBytes(file.size);

    const actionBtn = document.getElementById('action-btn') as HTMLButtonElement;
    if (actionBtn) actionBtn.disabled = false;

    this.renderLivePreview();
    if (this.mode === 'colors') this.extractAndShowColors();
    if (this.mode === 'favicon') this.renderFaviconSizes();
  }

  /** Build a synthetic File from raw markup so the whole pipeline can reuse it. */
  private setSvgFromString(content: string, name = 'pasted.svg'): void {
    const trimmed = content.trim();
    if (!/<svg[\s>]/i.test(trimmed)) {
      alert('That doesn’t look like SVG markup — it should contain an <svg> tag.');
      return;
    }
    const file = new File([trimmed], name, { type: 'image/svg+xml' });
    this.handleFileSelect(file);
  }

  /** Render the persistent before-processing preview + metadata for the loaded SVG. */
  private async renderLivePreview(): Promise<void> {
    if (!this.selectedFile || this.mode === 'sprite' || this.mode === 'trace') return;
    const wrap = document.getElementById('live-preview');
    const previewBox = document.getElementById('source-preview');
    const metaBox = document.getElementById('preview-meta');
    if (!wrap || !previewBox || !metaBox) return;

    const content = await this.selectedFile.text();
    wrap.classList.remove('hidden');

    // Render the SVG
    const doc = new DOMParser().parseFromString(content, 'image/svg+xml');
    previewBox.innerHTML = '';
    if (doc.documentElement.tagName !== 'parsererror') {
      const el = document.importNode(doc.documentElement, true) as unknown as SVGElement;
      previewBox.appendChild(el);
    } else {
      previewBox.innerHTML = '<span style="color:var(--muted);font-size:0.8rem;">Invalid SVG markup</span>';
    }

    // Metadata
    const root = doc.documentElement;
    let w = root.getAttribute('width') || '';
    let h = root.getAttribute('height') || '';
    const vb = root.getAttribute('viewBox');
    if ((!w || !h) && vb) { const p = vb.split(/[\s,]+/); w = w || p[2]; h = h || p[3]; }
    const elementCount = doc.querySelectorAll('*').length;
    const pathCount = doc.querySelectorAll('path').length;
    const rawBytes = new Blob([content]).size;
    const gz = await this.gzipSize(content);

    metaBox.innerHTML = `
      <div class="meta-item"><span class="meta-label">Dimensions</span><span class="meta-value">${w && h ? `${this.trimNum(w)} × ${this.trimNum(h)}` : (vb ? 'viewBox only' : '—')}</span></div>
      <div class="meta-item"><span class="meta-label">Elements</span><span class="meta-value">${elementCount}</span></div>
      <div class="meta-item"><span class="meta-label">Paths</span><span class="meta-value">${pathCount}</span></div>
      <div class="meta-item"><span class="meta-label">Raw size</span><span class="meta-value">${this.formatBytes(rawBytes)}</span></div>
      <div class="meta-item"><span class="meta-label">Gzipped</span><span class="meta-value good">${gz !== null ? this.formatBytes(gz) : 'n/a'}</span></div>
    `;
  }

  private trimNum(v: string): string {
    const n = parseFloat(v);
    return isNaN(n) ? v : String(parseFloat(n.toFixed(2)));
  }

  /** Real gzip size via the native CompressionStream API (graceful null fallback). */
  private async gzipSize(text: string): Promise<number | null> {
    try {
      if (typeof (window as any).CompressionStream === 'undefined') return null;
      const stream = new (window as any).CompressionStream('gzip');
      const writer = stream.writable.getWriter();
      writer.write(new TextEncoder().encode(text));
      writer.close();
      const compressed = await new Response(stream.readable).arrayBuffer();
      return compressed.byteLength;
    } catch { return null; }
  }

  private addSpriteFile(file: File): void {
    if (!file.name.endsWith('.svg')) return;
    if (this.spriteFiles.find(f => f.name === file.name)) return;
    this.spriteFiles.push(file);
    this.renderSpriteFileList();
    const actionBtn = document.getElementById('action-btn') as HTMLButtonElement;
    if (actionBtn) actionBtn.disabled = false;
  }

  private renderSpriteFileList(): void {
    const list = document.getElementById('sprite-file-list');
    if (!list) return;
    list.innerHTML = this.spriteFiles.map((f, i) => `
      <div class="sprite-file-item">
        <span class="sprite-file-name">📄 ${f.name}</span>
        <button class="sprite-file-remove" data-index="${i}">✕</button>
      </div>`).join('');
    list.querySelectorAll('.sprite-file-remove').forEach(btn => {
      btn.addEventListener('click', () => {
        const idx = parseInt((btn as HTMLElement).dataset.index!);
        this.spriteFiles.splice(idx, 1);
        this.renderSpriteFileList();
        const actionBtn = document.getElementById('action-btn') as HTMLButtonElement;
        if (actionBtn) actionBtn.disabled = this.spriteFiles.length === 0;
      });
    });
  }

  private async processFile(): Promise<void> {
    if (this.mode === 'trace') { await this.traceRaster(); return; }
    if (this.mode === 'favicon') { await this.generateFavicons(); return; }
    if (this.mode !== 'sprite' && !this.selectedFile) return;

    const actionBtn = document.getElementById('action-btn') as HTMLButtonElement;
    const spinner = document.getElementById('spinner') as HTMLElement;

    actionBtn.disabled = true;
    spinner.classList.remove('hidden');

    try {
      let result = '';

      switch (this.mode) {
        case 'minify':
          const content = await this.selectedFile.text();
          result = this.minifySVG(content);
          break;
        case 'convert':
          const convertContent = await this.selectedFile.text();
          result = this.convertSVG(convertContent);
          break;
        case 'optimize':
          result = await this.optimizeSVG();
          break;
        case 'extended':
          result = await this.generateComponentLocally();
          break;
        case 'visual':
          result = await this.visualEditLocally();
          break;
        case 'component':
          const componentContent = await this.selectedFile.text();
          result = this.generateComponent(componentContent);
          break;
        case 'export':
          await this.exportToRaster();
          return;
        case 'sprite':
          result = await this.generateSprite();
          break;
        case 'colors':
          result = await this.applyColorReplacements();
          break;
      }

      this.displayOutput(result);
    } catch (error) {
      console.error('Error processing file:', error);
      alert('Error processing SVG file: ' + (error as any).message);
    } finally {
      actionBtn.disabled = false;
      spinner.classList.add('hidden');
    }
  }

  private minifySVG(content: string): string {
    let minified = content;
    const removeMetadata = (document.getElementById('remove-metadata') as HTMLInputElement)?.checked;

    if (removeMetadata) {
      minified = minified.replace(/<\?xml[^?]*\?>/g, '');
      minified = minified.replace(/<!--.*?-->/gs, '');
      // Remove tool-specific namespace declarations but preserve xmlns="http://www.w3.org/2000/svg"
      minified = minified.replace(/\s+xmlns:[a-zA-Z0-9_-]+="[^"]*"/g, '');
    }

    minified = minified.replace(/\s+/g, ' ').trim();
    minified = minified.replace(/>\s+</g, '><');

    const originalSize = content.length;
    const minifiedSize = minified.length;
    const reduction = ((1 - minifiedSize / originalSize) * 100).toFixed(1);

    document.getElementById('optimized-size')!.textContent = this.formatBytes(minifiedSize);
    document.getElementById('reduction-percent')!.textContent = `${reduction}%`;

    return minified;
  }

  private convertSVG(content: string): string {
    const format = (document.getElementById('convert-format') as HTMLSelectElement)?.value || 'css';

    switch (format) {
      case 'datauri':
        return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(content)))}`;
      case 'inline':
        return content.replace(/"/g, '\\"');
      default: // css
        const encoded = encodeURIComponent(content);
        return `background-image: url("data:image/svg+xml,${encoded}");`;
    }
  }

  private generateComponent(content: string): string {
    const framework = (document.getElementById('component-framework') as HTMLSelectElement)?.value || 'react';
    const withProps = (document.getElementById('component-props') as HTMLInputElement)?.checked;

    let component = '';

    switch (framework) {
      case 'react':
        component = `export function SvgIcon({ width = 24, height = 24, color = "currentColor" }) {
  return (
    ${content.replace(/^<svg/, `<svg width={width} height={height}`)}
  );
}`;
        break;
      case 'vue':
        component = `<template>
  ${content}
</template>

<script setup lang="ts">
defineProps({
  width: { type: Number, default: 24 },
  height: { type: Number, default: 24 },
  color: { type: String, default: 'currentColor' }
});
</script>`;
        break;
      default: // html
        component = content;
    }

    return component;
  }

  private async optimizeLocally(): Promise<string> {
    const content = await this.selectedFile!.text();
    const removeComments  = (document.getElementById('opt-comments') as HTMLInputElement)?.checked;
    const removeMetadata  = (document.getElementById('opt-metadata') as HTMLInputElement)?.checked;
    const removeUnused    = (document.getElementById('opt-unused') as HTMLInputElement)?.checked;
    const precision       = parseInt((document.getElementById('opt-precision') as HTMLInputElement)?.value || '2');

    let svg = content;
    const originalSize = svg.length;

    if (removeComments)  svg = svg.replace(/<!--.*?-->/gs, '');
    if (removeMetadata) {
      svg = svg.replace(/<\?xml[^?]*\?>/g, '');
      svg = svg.replace(/<metadata[^>]*>.*?<\/metadata>/gs, '');
      svg = svg.replace(/\s+xmlns:[a-zA-Z0-9_-]+="[^"]*"/g, '');
      svg = svg.replace(/\s+(?:inkscape|sodipodi|dc|cc|rdf):[^=]+="[^"]*"/g, '');
      svg = svg.replace(/<(?:sodipodi|inkscape):[^/]*\/>/g, '');
    }
    if (removeUnused) {
      svg = svg.replace(/\s+overflow="visible"/g, '');
      svg = svg.replace(/\s+display="inline"/g, '');
      svg = svg.replace(/\s+enable-background="[^"]*"/g, '');
    }

    // Round decimals to requested precision
    svg = svg.replace(/\d+\.\d+/g, m => {
      const v = parseFloat(m);
      return parseFloat(v.toFixed(precision)).toString();
    });

    svg = svg.replace(/\s+/g, ' ').replace(/>\s+</g, '><').trim();

    const optimizedSize = svg.length;
    const reduction = ((1 - optimizedSize / originalSize) * 100).toFixed(1);
    document.getElementById('optimized-size')!.textContent = this.formatBytes(optimizedSize);
    document.getElementById('reduction-percent')!.textContent = `${reduction}%`;

    return svg;
  }

  /** Optimize entry point: deep SVGO when toggled (falls back to the local optimizer). */
  private async optimizeSVG(): Promise<string> {
    const useSvgo = (document.getElementById('opt-svgo') as HTMLInputElement)?.checked;
    if (!useSvgo) return this.optimizeLocally();

    const content = await this.selectedFile!.text();
    const originalSize = new Blob([content]).size;
    let result: string;
    try {
      result = await this.optimizeWithSVGO(content);
    } catch (e) {
      console.warn('SVGO unavailable, using local optimizer:', e);
      return this.optimizeLocally();
    }
    const optimizedSize = new Blob([result]).size;
    const reduction = ((1 - optimizedSize / originalSize) * 100).toFixed(1);
    document.getElementById('optimized-size')!.textContent = this.formatBytes(optimizedSize);
    document.getElementById('reduction-percent')!.textContent = `${reduction}%`;
    return result;
  }

  private async optimizeWithSVGO(content: string): Promise<string> {
    const optimize = await this.loadSVGO();
    const precision = parseInt((document.getElementById('opt-precision') as HTMLInputElement)?.value || '2');
    const res = optimize(content, {
      multipass: true,
      floatPrecision: precision,
      plugins: [
        { name: 'preset-default', params: { overrides: { removeViewBox: false } } },
      ],
    });
    return res.data;
  }

  /** Load SVGO's ESM browser bundle via an injected module script (bypasses the webpack graph). */
  private loadSVGO(): Promise<any> {
    if ((window as any).__svgoOptimize) return Promise.resolve((window as any).__svgoOptimize);
    if (this.svgoPromise) return this.svgoPromise;
    this.svgoPromise = new Promise((resolve, reject) => {
      const ready = () => resolve((window as any).__svgoOptimize);
      window.addEventListener('svgo-ready', ready, { once: true });
      const s = document.createElement('script');
      s.type = 'module';
      s.textContent =
        "import { optimize } from 'https://cdn.jsdelivr.net/npm/svgo@3.3.2/dist/svgo.browser.js';" +
        "window.__svgoOptimize = optimize;" +
        "window.dispatchEvent(new Event('svgo-ready'));";
      document.head.appendChild(s);
      setTimeout(() => { if (!(window as any).__svgoOptimize) reject(new Error('SVGO load timed out')); }, 12000);
    });
    return this.svgoPromise;
  }

  /* ----------------------------- Raster → SVG (trace) ----------------------------- */

  private loadImageTracer(): Promise<any> {
    if ((window as any).ImageTracer) return Promise.resolve((window as any).ImageTracer);
    return new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/imagetracerjs@1.2.6/imagetracer_v1.2.6.js';
      s.onload = () => resolve((window as any).ImageTracer);
      s.onerror = () => reject(new Error('Failed to load the tracing engine'));
      document.head.appendChild(s);
    });
  }

  private traceOptions(preset: string, numColors: number): any {
    const base: any = { numberofcolors: numColors, ltres: 1, qtres: 1, pathomit: 8, colorsampling: 1, mincolorratio: 0, scale: 1, roundcoords: 1 };
    switch (preset) {
      case 'detailed': return { ...base, ltres: 0.5, qtres: 0.5, pathomit: 0, numberofcolors: Math.max(numColors, 16) };
      case 'poster':   return { ...base, numberofcolors: Math.min(numColors, 6), pathomit: 16, blurradius: 2 };
      case 'bw':       return { ...base, numberofcolors: 2, colorsampling: 0, pathomit: 4 };
      default:         return base; // flat
    }
  }

  private async traceRaster(): Promise<void> {
    if (!this.rasterFile) return;
    const actionBtn = document.getElementById('action-btn') as HTMLButtonElement;
    const spinner = document.getElementById('spinner') as HTMLElement;
    actionBtn.disabled = true; spinner.classList.remove('hidden');
    try {
      const ImageTracer = await this.loadImageTracer();
      const dataUrl = await this.fileToDataURL(this.rasterFile);
      const preset = (document.getElementById('trace-preset') as HTMLSelectElement).value;
      const numColors = parseInt((document.getElementById('trace-colors') as HTMLInputElement).value);
      const options = this.traceOptions(preset, numColors);
      const svg: string = await new Promise((resolve) => {
        ImageTracer.imageToSVG(dataUrl, (svgstr: string) => resolve(svgstr), options);
      });
      this.displayOutput(svg);
    } catch (e) {
      alert('Tracing failed: ' + ((e as any).message || e));
    } finally {
      actionBtn.disabled = false; spinner.classList.add('hidden');
    }
  }

  private fileToDataURL(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result as string);
      r.onerror = () => reject(new Error('Could not read file'));
      r.readAsDataURL(file);
    });
  }

  /* ----------------------------- Favicon package ----------------------------- */

  private renderFaviconSizes(): void {
    const el = document.getElementById('favicon-sizes');
    if (!el) return;
    const items = ['favicon.ico', 'favicon.svg', '16', '32', '48', '64', '128', '180', '192', '256', '512', 'manifest'];
    el.innerHTML = items.map(s => `<span class="favicon-chip">${/^\d+$/.test(s) ? s + '×' + s : s}</span>`).join('');
  }

  private rasterizeSvgToBlob(content: string, size: number, bg: string | null, padding: boolean): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext('2d')!;
      if (bg) { ctx.fillStyle = bg; ctx.fillRect(0, 0, size, size); }
      const img = new Image();
      img.onload = () => {
        const pad = padding ? Math.round(size * 0.1) : 0;
        ctx.drawImage(img, pad, pad, size - pad * 2, size - pad * 2);
        canvas.toBlob(b => b ? resolve(b) : reject(new Error('Canvas export failed')), 'image/png');
      };
      img.onerror = () => reject(new Error('Could not render SVG to canvas'));
      img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(content)))}`;
    });
  }

  /** Build a real .ico that embeds PNG entries (valid for all modern browsers). */
  private async buildIco(entries: { size: number; blob: Blob }[]): Promise<Blob> {
    const imgs = await Promise.all(entries.map(async e => ({ size: e.size, data: new Uint8Array(await e.blob.arrayBuffer()) })));
    const count = imgs.length;
    const header = new Uint8Array(6 + count * 16);
    const dv = new DataView(header.buffer);
    dv.setUint16(0, 0, true);      // reserved
    dv.setUint16(2, 1, true);      // type: icon
    dv.setUint16(4, count, true);  // image count
    let offset = header.length;
    imgs.forEach((img, i) => {
      const o = 6 + i * 16;
      const dim = img.size >= 256 ? 0 : img.size;
      header[o] = dim;                          // width
      header[o + 1] = dim;                      // height
      header[o + 2] = 0;                        // palette
      header[o + 3] = 0;                        // reserved
      dv.setUint16(o + 4, 1, true);             // color planes
      dv.setUint16(o + 6, 32, true);            // bits per pixel
      dv.setUint32(o + 8, img.data.length, true); // size of image data
      dv.setUint32(o + 12, offset, true);       // offset to image data
      offset += img.data.length;
    });
    return new Blob([header, ...imgs.map(i => i.data)], { type: 'image/x-icon' });
  }

  private async generateFavicons(): Promise<void> {
    if (!this.selectedFile) return;
    const actionBtn = document.getElementById('action-btn') as HTMLButtonElement;
    const spinner = document.getElementById('spinner') as HTMLElement;
    actionBtn.disabled = true; spinner.classList.remove('hidden');
    try {
      const content = await this.selectedFile.text();
      const padding = (document.getElementById('favicon-padding') as HTMLInputElement)?.checked;
      const bgText = ((document.getElementById('favicon-bg-text') as HTMLInputElement)?.value || 'transparent').trim();
      const bg = bgText.toLowerCase() === 'transparent' || bgText === '' ? null : bgText;

      const zip = new JSZip();
      const pngSizes = [16, 32, 48, 64, 128, 180, 192, 256, 512];
      const blobs: Record<number, Blob> = {};
      for (const size of pngSizes) {
        const blob = await this.rasterizeSvgToBlob(content, size, bg, padding);
        blobs[size] = blob;
        const name =
          size === 180 ? 'apple-touch-icon.png' :
          size === 192 ? 'android-chrome-192x192.png' :
          size === 512 ? 'android-chrome-512x512.png' :
          `favicon-${size}x${size}.png`;
        zip.file(name, blob);
      }

      const ico = await this.buildIco([
        { size: 16, blob: blobs[16] },
        { size: 32, blob: blobs[32] },
        { size: 48, blob: blobs[48] },
      ]);
      zip.file('favicon.ico', ico);
      zip.file('favicon.svg', content);

      const manifest = {
        name: 'My Application', short_name: 'App',
        icons: [
          { src: '/android-chrome-192x192.png', sizes: '192x192', type: 'image/png' },
          { src: '/android-chrome-512x512.png', sizes: '512x512', type: 'image/png' },
        ],
        theme_color: bg || '#ffffff', background_color: bg || '#ffffff', display: 'standalone',
      };
      zip.file('site.webmanifest', JSON.stringify(manifest, null, 2));

      const html = [
        '<!-- Place these in your <head> -->',
        '<link rel="icon" href="/favicon.ico" sizes="any">',
        '<link rel="icon" href="/favicon.svg" type="image/svg+xml">',
        '<link rel="apple-touch-icon" href="/apple-touch-icon.png">',
        '<link rel="manifest" href="/site.webmanifest">',
      ].join('\n');
      zip.file('head-snippet.html', html);
      zip.file('README.txt', 'Drop these files in your site root.\nPaste the contents of head-snippet.html into your <head>.\nGenerated by svg.ignlab.net');

      const out = await zip.generateAsync({ type: 'blob' });
      const baseName = this.selectedFile.name.replace(/\.svg$/i, '') || 'favicon';
      this.downloadBlob(out, `${baseName}-favicons.zip`);
      this.displayOutput(html);
    } catch (e) {
      alert('Favicon generation failed: ' + ((e as any).message || e));
    } finally {
      actionBtn.disabled = false; spinner.classList.add('hidden');
    }
  }

  private downloadBlob(blob: Blob, name: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  }

  private async generateComponentLocally(): Promise<string> {
    const content = await this.selectedFile!.text();
    const framework = (document.getElementById('extended-framework') as HTMLSelectElement)?.value || 'react';
    const withProps = (document.getElementById('extended-props') as HTMLInputElement)?.checked;

    switch (framework) {
      case 'react': {
        let svg = content.replace(/\s+width="\d+[^"]*"/, '').replace(/\s+height="\d+[^"]*"/, '');
        svg = svg.replace('<svg', '<svg width={width} height={height}');
        return withProps
          ? `export function SvgIcon({ width = 24, height = 24, color = "currentColor" }) {\n  return (\n    ${svg}\n  );\n}`
          : `export default function SvgIcon() {\n  return (\n    ${content}\n  );\n}`;
      }
      case 'vue':
        return withProps
          ? `<template>\n  ${content}\n</template>\n\n<script setup lang="ts">\ndefineProps({\n  width: { type: Number, default: 24 },\n  height: { type: Number, default: 24 },\n  color: { type: String, default: 'currentColor' }\n});\n</script>`
          : `<template>\n  ${content}\n</template>\n\n<script setup lang="ts">\n</script>`;
      case 'svelte':
        return withProps
          ? `<script>\n  export let width = 24;\n  export let height = 24;\n  export let color = 'currentColor';\n</script>\n\n${content}`
          : content;
      case 'flutter': {
        const encoded = btoa(unescape(encodeURIComponent(content)));
        return `// pubspec.yaml: flutter_svg: ^2.0.0\nimport 'package:flutter/material.dart';\nimport 'package:flutter_svg/flutter_svg.dart';\n\nclass SvgIcon extends StatelessWidget {\n  final double width;\n  final double height;\n  final Color? color;\n\n  const SvgIcon({super.key, this.width = 24, this.height = 24, this.color});\n\n  static const String _svg = '''\n${content}\n''';\n\n  @override\n  Widget build(BuildContext context) {\n    return SvgPicture.string(_svg, width: width, height: height,\n      colorFilter: color != null ? ColorFilter.mode(color!, BlendMode.srcIn) : null);\n  }\n}`;
      }
      case 'compose': {
        const encoded = btoa(unescape(encodeURIComponent(content)));
        return `// implementation("io.coil-kt:coil-compose:2.x.x")\nimport androidx.compose.runtime.Composable\nimport androidx.compose.ui.Modifier\n\nprivate const val SVG_DATA = "data:image/svg+xml;base64,${encoded}"\n\n@Composable\nfun SvgIcon(modifier: Modifier = Modifier) {\n    AsyncImage(model = SVG_DATA, contentDescription = null, modifier = modifier)\n}`;
      }
      case 'swiftui':
        return `import SwiftUI\nimport WebKit\n\nstruct SvgIcon: UIViewRepresentable {\n  var width: CGFloat = 24\n  var height: CGFloat = 24\n\n  private let svg = """\n${content}\n"""\n\n  func makeUIView(context: Context) -> WKWebView {\n    let v = WKWebView()\n    v.isOpaque = false\n    v.backgroundColor = .clear\n    v.loadHTMLString("<html><body style='margin:0'>\\(svg)</body></html>", baseURL: nil)\n    return v\n  }\n  func updateUIView(_ v: WKWebView, context: Context) {}\n}`;
      default:
        return content;
    }
  }

  private async visualEditLocally(): Promise<string> {
    const operation = (document.getElementById('visual-operation') as HTMLSelectElement)?.value || 'edit-attributes';
    const svgContent = await this.selectedFile!.text();

    if (operation === 'viewbox-fixer') {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgContent, 'image/svg+xml');
      const root = doc.documentElement;
      const w = parseFloat(root.getAttribute('width') || '0');
      const h = parseFloat(root.getAttribute('height') || '0');
      if (w && h && !root.getAttribute('viewBox')) {
        root.setAttribute('viewBox', `0 0 ${w} ${h}`);
      }
      return new XMLSerializer().serializeToString(root);
    }

    // edit-attributes: extract editable attrs and open editor
    const EDITABLE = new Set(['fill','stroke','stroke-width','opacity','width','height','rx','ry','r','cx','cy','x','y','font-size','transform']);
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgContent, 'image/svg+xml');
    const attributes: Record<string, any[]> = {};

    doc.documentElement.querySelectorAll('*').forEach(el => {
      const tag = el.tagName.toLowerCase();
      if (['svg','defs','style','title','desc'].includes(tag)) return;
      const editable: Record<string, string> = {};
      el.getAttributeNames().forEach(a => { if (EDITABLE.has(a)) editable[a] = el.getAttribute(a)!; });
      if (Object.keys(editable).length) {
        if (!attributes[tag]) attributes[tag] = [];
        attributes[tag].push({ id: el.getAttribute('id') || 'unnamed', attributes: editable });
      }
    });

    this.renderAttributeEditor(attributes, svgContent);
    return '';
  }

  private renderAttributeEditor(attributes: any, svgContent: string): void {
    const editorDiv = document.getElementById('editor-controls');
    if (!editorDiv) return;

    editorDiv.innerHTML = '';
    (window as any).svgAttributes = attributes; // Store for later use
    (window as any).svgContent = svgContent; // Store original SVG

    // Display initial preview
    this.updateSVGPreview(svgContent);

    // Group attributes by element type
    Object.keys(attributes).forEach(elementType => {
      const elements = attributes[elementType];

      elements.forEach((elem: any, index: number) => {
        const groupDiv = document.createElement('div');
        groupDiv.className = 'attr-group';
        groupDiv.innerHTML = `<div class="attr-title">${elementType}${elem.id !== 'unnamed' ? ' #' + elem.id : ''}</div>`;

        // Create input for each attribute
        Object.entries(elem.attributes).forEach(([key, value]: [string, any]) => {
          const inputGroup = document.createElement('div');
          inputGroup.className = 'attr-input-group';

          // Color attributes get color picker
          if (key === 'fill' || key === 'stroke') {
            inputGroup.innerHTML = `
              <div class="attr-label">
                <span>${key}</span>
                <span class="attr-value">${value}</span>
              </div>
              <div class="color-input-wrapper">
                <input type="color" class="color-picker" data-element="${elementType}" data-index="${index}" data-attr="${key}" value="${this.hexToColor(value)}">
                <input type="text" class="attr-input" data-element="${elementType}" data-index="${index}" data-attr="${key}" value="${value}">
              </div>
            `;
          }
          // Numeric attributes get slider + input
          else if (!isNaN(Number(value)) && key !== 'cx' && key !== 'cy' && key !== 'r') {
            const numValue = parseFloat(value);
            inputGroup.innerHTML = `
              <div class="attr-label">
                <span>${key}</span>
                <span class="attr-value">${value}</span>
              </div>
              <input type="range" class="slider" data-element="${elementType}" data-index="${index}" data-attr="${key}" min="0" max="${numValue * 2}" step="0.1" value="${numValue}">
            `;
          }
          // Text attributes
          else {
            inputGroup.innerHTML = `
              <div class="attr-label"><span>${key}</span></div>
              <input type="text" class="attr-input" data-element="${elementType}" data-index="${index}" data-attr="${key}" value="${value}">
            `;
          }

          groupDiv.appendChild(inputGroup);
        });

        editorDiv.appendChild(groupDiv);
      });
    });

    // Attach event listeners
    editorDiv.querySelectorAll('input').forEach(input => {
      input.addEventListener('input', () => this.updateAttributePreview());
    });

    // Show editor and attach buttons
    document.getElementById('attribute-editor')?.classList.remove('hidden');
    document.getElementById('output-section')?.classList.add('hidden');
    this.refreshSliders(editorDiv);

    const applyBtn = document.getElementById('apply-changes');
    const resetBtn = document.getElementById('reset-changes');

    applyBtn?.addEventListener('click', () => this.applyAttributeChanges());
    resetBtn?.addEventListener('click', () => this.resetAttributeEditor());
  }

  private updateAttributePreview(): void {
    // Update the display values as user types
    const inputs = document.querySelectorAll('[data-attr]');
    inputs.forEach(input => {
      const container = input.closest('.attr-input-group');
      const label = container?.querySelector('.attr-value');
      if (label && input instanceof HTMLInputElement) {
        label.textContent = input.value;
      }
    });

    // Update SVG preview with new values
    const updatedSvg = this.buildPreviewSVG();
    this.updateSVGPreview(updatedSvg);
  }

  private buildPreviewSVG(): string {
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString((window as any).svgContent, 'image/svg+xml');

    if (svgDoc.documentElement.tagName === 'parsererror') {
      return (window as any).svgContent; // Return original if parse fails
    }

    // Apply current attribute values from inputs
    const inputs = document.querySelectorAll('[data-attr]');
    let elementIndex = 0;
    const elementCounts: any = {};

    inputs.forEach(input => {
      if (input instanceof HTMLInputElement) {
        const elementType = input.getAttribute('data-element');
        const attrIndex = parseInt(input.getAttribute('data-index') || '0');
        const attr = input.getAttribute('data-attr');
        const value = input.value;

        // Find and update the specific element
        const elements = svgDoc.getElementsByTagName(elementType);
        if (elements[attrIndex]) {
          elements[attrIndex].setAttribute(attr, value);
        }
      }
    });

    // Serialize back to string
    const serializer = new XMLSerializer();
    return serializer.serializeToString(svgDoc.documentElement);
  }

  private updateSVGPreview(svgContent: string): void {
    const previewDiv = document.getElementById('svg-preview');
    if (!previewDiv) return;

    // Clear previous content
    previewDiv.innerHTML = '';

    // Create and render SVG element
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');

    if (svgDoc.documentElement.tagName !== 'parsererror') {
      const svgElement = document.importNode(svgDoc.documentElement, true);
      previewDiv.appendChild(svgElement);
    } else {
      previewDiv.innerHTML = '<div style="color: var(--muted); padding: 20px; text-align: center;">Invalid SVG</div>';
    }
  }

  private applyAttributeChanges(): void {
    const updates: any = {};
    const inputs = document.querySelectorAll('[data-attr]');

    inputs.forEach(input => {
      if (input instanceof HTMLInputElement) {
        const element = input.getAttribute('data-element');
        const index = parseInt(input.getAttribute('data-index') || '0');
        const attr = input.getAttribute('data-attr');

        if (!updates[element]) updates[element] = [];
        if (!updates[element][index]) updates[element][index] = {};

        updates[element][index][attr] = input.value;
      }
    });

    // Call backend to apply changes
    this.applyAttributeUpdates(updates);
  }

  private async applyAttributeUpdates(updates: any): Promise<void> {
    const svgContent = (window as any).svgContent as string;
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgContent, 'image/svg+xml');

    Object.entries(updates).forEach(([tag, elems]: [string, any]) => {
      const elements = doc.documentElement.querySelectorAll(tag);
      Object.entries(elems).forEach(([idx, attrs]: [string, any]) => {
        const el = elements[parseInt(idx)];
        if (el) Object.entries(attrs).forEach(([attr, val]) => el.setAttribute(attr, val as string));
      });
    });

    const result = new XMLSerializer().serializeToString(doc.documentElement);
    document.getElementById('attribute-editor')?.classList.add('hidden');
    this.displayOutput(result);
  }

  private resetAttributeEditor(): void {
    const operation = (document.getElementById('visual-operation') as HTMLSelectElement)?.value || 'edit-attributes';
    if (operation === 'edit-attributes') {
      // Re-run visual edit to reset
      this.processFile();
    }
  }

  private hexToColor(value: string): string {
    // Convert hex or rgb to hex for color input
    if (value.startsWith('#')) return value;
    if (value.startsWith('rgb')) {
      const match = value.match(/\d+/g);
      if (match) {
        return '#' + match.slice(0, 3).map(x => parseInt(x).toString(16).padStart(2, '0')).join('');
      }
    }
    return '#000000';
  }

  private displayOutput(result: string): void {
    const outputSection = document.getElementById('output-section');
    const outputCode = document.getElementById('output-code');

    if (outputCode) {
      outputCode.textContent = result;
      outputSection?.classList.remove('hidden');
    }
  }

  private copyToClipboard(): void {
    const code = document.getElementById('output-code')?.textContent;
    if (code) {
      navigator.clipboard.writeText(code).then(() => {
        const btn = document.getElementById('copy-btn') as HTMLButtonElement;
        const originalText = btn.textContent;
        btn.textContent = '✓ Copied!';
        setTimeout(() => {
          btn.textContent = originalText;
        }, 2000);
      });
    }
  }

  private async exportToRaster(): Promise<void> {
    const content = await this.selectedFile!.text();
    const format = (document.getElementById('export-format') as HTMLSelectElement).value;
    const scale = parseFloat((document.getElementById('export-scale') as HTMLInputElement).value);
    const transparent = (document.getElementById('export-transparent') as HTMLInputElement).checked;

    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'image/svg+xml');
    const svgEl = doc.documentElement;

    let w = parseFloat(svgEl.getAttribute('width') || '0');
    let h = parseFloat(svgEl.getAttribute('height') || '0');
    if (!w || !h) {
      const vb = svgEl.getAttribute('viewBox')?.split(/[\s,]+/);
      w = parseFloat(vb?.[2] || '300');
      h = parseFloat(vb?.[3] || '300');
    }

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    const ctx = canvas.getContext('2d')!;

    if (!transparent || format === 'jpeg') {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    const dataUri = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(content)))}`;
    await new Promise<void>((resolve, reject) => {
      const img = new Image();
      img.onload = () => { ctx.drawImage(img, 0, 0, canvas.width, canvas.height); resolve(); };
      img.onerror = reject;
      img.src = dataUri;
    });

    const mimeType = format === 'jpeg' ? 'image/jpeg' : format === 'webp' ? 'image/webp' : 'image/png';
    const previewUrl = canvas.toDataURL(mimeType, 0.92);

    // Show preview
    const previewSection = document.getElementById('export-preview-section');
    const previewImg = document.getElementById('export-preview-img') as HTMLImageElement;
    const previewInfo = document.getElementById('export-preview-info');
    if (previewImg) previewImg.src = previewUrl;
    if (previewInfo) previewInfo.textContent = `${canvas.width} × ${canvas.height}px · ${format.toUpperCase()}`;
    previewSection?.classList.remove('hidden');

    // Trigger download
    canvas.toBlob(blob => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.selectedFile!.name.replace(/\.svg$/i, '')}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    }, mimeType, 0.92);
  }

  private async generateSprite(): Promise<string> {
    const usePrefix = (document.getElementById('sprite-prefix') as HTMLInputElement)?.checked;
    const symbols: string[] = [];
    const usages: string[] = [];

    for (const file of this.spriteFiles) {
      const content = await file.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(content, 'image/svg+xml');
      const svgEl = doc.documentElement;

      const viewBox = svgEl.getAttribute('viewBox') ||
        `0 0 ${svgEl.getAttribute('width') || 24} ${svgEl.getAttribute('height') || 24}`;

      const baseName = file.name.replace(/\.svg$/i, '').replace(/[^a-zA-Z0-9_-]/g, '-');
      const id = usePrefix ? `icon-${baseName}` : baseName;

      // Extract inner content (skip <defs> hoisting for simplicity)
      const inner = Array.from(svgEl.childNodes)
        .map(n => new XMLSerializer().serializeToString(n))
        .join('');

      symbols.push(`  <symbol id="${id}" viewBox="${viewBox}">\n    ${inner.trim()}\n  </symbol>`);
      usages.push(`<!-- <svg><use href="#${id}"/></svg> -->`);
    }

    return [
      `<svg xmlns="http://www.w3.org/2000/svg" style="display:none">`,
      symbols.join('\n'),
      `</svg>`,
      ``,
      `<!-- Usage examples:`,
      ...usages,
      `-->`
    ].join('\n');
  }

  private async extractAndShowColors(): Promise<void> {
    if (!this.selectedFile) return;
    const content = await this.selectedFile.text();
    (window as any).svgOriginal = content;
    (window as any).svgColors = {};

    const colors = this.extractColors(content);
    const palette = document.getElementById('color-palette');
    if (!palette) return;
    palette.innerHTML = '';

    if (colors.size === 0) {
      palette.innerHTML = '<span style="font-size:0.82rem;color:var(--muted);">No colors found</span>';
      return;
    }

    colors.forEach(color => {
      const swatch = document.createElement('div');
      swatch.className = 'color-swatch';
      swatch.style.background = color;
      swatch.title = color;
      swatch.dataset.original = color;
      swatch.addEventListener('click', () => this.selectColorForReplace(color, swatch));
      palette.appendChild(swatch);
    });

    this.updateColorPreview();
  }

  private extractColors(svg: string): Set<string> {
    const colors = new Set<string>();
    const colorRegex = /#[0-9a-fA-F]{3,8}|rgb\([^)]+\)|rgba\([^)]+\)|hsl\([^)]+\)|hsla\([^)]+\)/g;

    // From attributes
    const attrRegex = /(?:fill|stroke|stop-color|color)="([^"]+)"/g;
    let m;
    while ((m = attrRegex.exec(svg)) !== null) {
      const v = m[1].trim();
      if (v && v !== 'none' && v !== 'currentColor' && v !== 'inherit') colors.add(v);
    }

    // From style attributes
    const styleRegex = /style="([^"]*)"/g;
    while ((m = styleRegex.exec(svg)) !== null) {
      const cm = m[1].match(colorRegex);
      cm?.forEach(c => colors.add(c));
    }

    return colors;
  }

  private selectColorForReplace(color: string, swatch: HTMLElement): void {
    document.querySelectorAll('.color-swatch').forEach(s => s.classList.remove('selected'));
    swatch.classList.add('selected');

    const form = document.getElementById('color-replace-form');
    const previewSwatch = document.getElementById('color-replace-preview');
    const source = document.getElementById('color-replace-source');
    const picker = document.getElementById('color-replace-picker') as HTMLInputElement;
    const text = document.getElementById('color-replace-text') as HTMLInputElement;

    const currentReplacement = (window as any).svgColors?.[color] ?? color;
    if (previewSwatch) previewSwatch.style.background = currentReplacement;
    if (source) source.textContent = color;
    if (picker) picker.value = this.toHex(currentReplacement);
    if (text) text.value = currentReplacement;
    form?.classList.remove('hidden');

    // Remove old listeners by replacing elements
    const newPicker = picker.cloneNode(true) as HTMLInputElement;
    const newText = text.cloneNode(true) as HTMLInputElement;
    picker.replaceWith(newPicker);
    text.replaceWith(newText);

    const onColorChange = () => {
      const val = (document.getElementById('color-replace-text') as HTMLInputElement).value.trim() || color;
      // Live: update colorMap and refresh preview instantly
      const colorMap = (window as any).svgColors || {};
      colorMap[color] = val;
      (window as any).svgColors = colorMap;
      if (previewSwatch) previewSwatch.style.background = val;
      swatch.style.background = val;
      swatch.title = val !== color ? `${color} → ${val}` : color;
      this.updateColorPreview();
    };

    // Sync picker → text
    document.getElementById('color-replace-picker')?.addEventListener('input', (e) => {
      (document.getElementById('color-replace-text') as HTMLInputElement).value = (e.target as HTMLInputElement).value;
      onColorChange();
    });
    // Sync text → picker + live update
    document.getElementById('color-replace-text')?.addEventListener('input', (e) => {
      try { (document.getElementById('color-replace-picker') as HTMLInputElement).value = this.toHex((e.target as HTMLInputElement).value); } catch {}
      onColorChange();
    });

    // Apply button — commits the change (already live, just visual confirmation)
    const btn = document.getElementById('color-replace-btn');
    btn?.replaceWith(btn.cloneNode(true));
    document.getElementById('color-replace-btn')?.addEventListener('click', () => {
      onColorChange();
      // Flash the swatch to confirm
      swatch.style.transform = 'scale(1.3)';
      setTimeout(() => { swatch.style.transform = ''; }, 200);
    });
  }

  private updateColorPreview(): void {
    const original: string = (window as any).svgOriginal;
    if (!original) return;
    const colorMap: Record<string, string> = (window as any).svgColors || {};

    let svg = original;
    Object.entries(colorMap).forEach(([from, to]) => {
      if (from !== to) svg = svg.split(from).join(to);
    });

    const previewDiv = document.getElementById('color-svg-preview');
    if (!previewDiv) return;
    const parser = new DOMParser();
    const doc = parser.parseFromString(svg, 'image/svg+xml');
    if (doc.documentElement.tagName === 'parsererror') return;
    const svgEl = document.importNode(doc.documentElement, true);
    // Make SVG scale to fit container
    svgEl.style.maxWidth = '100%';
    svgEl.style.maxHeight = '100%';
    svgEl.removeAttribute('width');
    svgEl.removeAttribute('height');
    previewDiv.innerHTML = '';
    previewDiv.appendChild(svgEl);
  }

  private async applyColorReplacements(): Promise<string> {
    const content = await this.selectedFile!.text();
    const colorMap: Record<string, string> = (window as any).svgColors || {};
    let result = content;
    Object.entries(colorMap).forEach(([from, to]) => {
      if (from !== to) result = result.split(from).join(to);
    });
    return result;
  }

  private toHex(color: string): string {
    if (color.startsWith('#')) {
      if (color.length === 4) {
        return '#' + color.slice(1).split('').map(c => c + c).join('');
      }
      return color.slice(0, 7);
    }
    const m = color.match(/\d+/g);
    if (m) return '#' + m.slice(0, 3).map(x => parseInt(x).toString(16).padStart(2, '0')).join('');
    return '#000000';
  }

  private downloadOutput(): void {
    const code = document.getElementById('output-code')?.textContent;
    if (!code) return;

    const extMap: Record<string, string> = {
      minify: 'svg',
      optimize: 'svg',
      convert: 'txt',
      component: 'jsx',
      extended: 'jsx',
      visual: 'svg',
      sprite: 'svg',
      colors: 'svg',
      trace: 'svg',
      export: 'txt',
    };
    let finalExt = extMap[this.mode] || 'txt';

    // The framework extension only applies when exporting components — the
    // extended-framework <select> exists (hidden) in every mode, so guard by mode.
    if (this.mode === 'extended' || this.mode === 'component') {
      const frameworkExtMap: Record<string, string> = {
        react: 'jsx', vue: 'vue', svelte: 'svelte', flutter: 'dart', compose: 'kt', swiftui: 'swift', html: 'html'
      };
      const framework = (document.getElementById('extended-framework') as HTMLSelectElement)?.value;
      if (framework && frameworkExtMap[framework]) finalExt = frameworkExtMap[framework];
    }

    const mime = finalExt === 'svg' ? 'image/svg+xml' : 'text/plain';
    const blob = new Blob([code], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    // Trace uses the raster file as its source; every other mode uses selectedFile.
    const srcName = this.mode === 'trace' ? this.rasterFile?.name : this.selectedFile?.name;
    const baseName = this.mode === 'sprite' ? 'sprite' : (srcName?.replace(/\.[^.]+$/, '') || 'output');
    a.href = url;
    a.download = `${baseName}.${finalExt}`;
    a.click();
    URL.revokeObjectURL(url);
  }

  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }
}

// Initialize on load
window.addEventListener('DOMContentLoaded', () => {
  new SVGTools();
});
